import React, { useState, useEffect } from 'react';
import { Button, Form, Alert, Spinner } from 'react-bootstrap';
import { Lock, User, Eye, EyeOff, Package } from 'lucide-react';
import useAuth from '../../hooks/useAuth';
import useForm from '../../hooks/useForm';
import { checkAndShowSessionExpiredMessage } from '../../utils/api';

const Login = () => {
    const { login } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    const { values, handleChange } = useForm({ username: '', password: '' });

    useEffect(() => {
        checkAndShowSessionExpiredMessage();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
            const result = await login(values.username, values.password);
            if (!result.success) setError(result.message);
        } catch (err) {
            setError("Une erreur inattendue s'est produite. Veuillez réessayer.");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="d-flex align-items-center justify-content-center p-3"
            style={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)',
            }}
        >
            <div
                className="bg-white shadow-lg overflow-hidden"
                style={{ width: '100%', maxWidth: '900px', borderRadius: '1rem' }}
            >
                <div className="row g-0">
                    {/* Panneau marque (desktop) */}
                    <div
                        className="col-lg-5 d-none d-lg-flex flex-column justify-content-between text-white p-5"
                        style={{ background: 'linear-gradient(160deg, #1e3c72 0%, #2a5298 100%)' }}
                    >
                        <div>
                            <div
                                className="d-inline-flex align-items-center justify-content-center mb-4"
                                style={{ width: 56, height: 56, borderRadius: 16, background: 'rgba(255,255,255,0.15)' }}
                            >
                                <Package size={30} />
                            </div>
                            <h3 className="fw-bold mb-2">Soplast Group</h3>
                            <p className="mb-0" style={{ opacity: 0.85 }}>
                                Système de gestion des commandes et de la distribution.
                            </p>
                        </div>
                        <div style={{ opacity: 0.6, fontSize: '0.85rem' }}>
                            © {new Date().getFullYear()} Senegal Soplast SARL
                        </div>
                    </div>

                    {/* Panneau formulaire */}
                    <div className="col-lg-7">
                        <div className="p-4 p-md-5">
                            <div className="text-center mb-4">
                                <div
                                    className="d-lg-none d-inline-flex align-items-center justify-content-center mb-3 text-white"
                                    style={{ width: 56, height: 56, borderRadius: 16, background: 'linear-gradient(160deg, #1e3c72, #2a5298)' }}
                                >
                                    <Package size={30} />
                                </div>
                                <h4 className="fw-bold mb-1">Bienvenue</h4>
                                <p className="text-muted mb-0">Connectez-vous à votre espace</p>
                            </div>

                            {error && <Alert variant="danger" className="py-2">{error}</Alert>}

                            <Form onSubmit={handleSubmit}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="small fw-semibold text-secondary">
                                        Nom d'utilisateur
                                    </Form.Label>
                                    <div className="input-group">
                                        <span className="input-group-text bg-light border-end-0 text-muted">
                                            <User size={18} />
                                        </span>
                                        <Form.Control
                                            type="text"
                                            name="username"
                                            value={values.username}
                                            onChange={handleChange}
                                            placeholder="Entrer votre nom d'utilisateur"
                                            className="border-start-0 ps-0"
                                            required
                                            disabled={loading}
                                            autoFocus
                                        />
                                    </div>
                                </Form.Group>

                                <Form.Group className="mb-4">
                                    <Form.Label className="small fw-semibold text-secondary">
                                        Mot de passe
                                    </Form.Label>
                                    <div className="input-group">
                                        <span className="input-group-text bg-light border-end-0 text-muted">
                                            <Lock size={18} />
                                        </span>
                                        <Form.Control
                                            type={showPassword ? 'text' : 'password'}
                                            name="password"
                                            value={values.password}
                                            onChange={handleChange}
                                            placeholder="Entrer votre mot de passe"
                                            className="border-start-0 border-end-0 ps-0"
                                            required
                                            disabled={loading}
                                        />
                                        <span
                                            className="input-group-text bg-light border-start-0 text-muted"
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => setShowPassword((s) => !s)}
                                        >
                                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </span>
                                    </div>
                                </Form.Group>

                                <Button
                                    type="submit"
                                    className="w-100 fw-semibold border-0 py-2"
                                    disabled={loading}
                                    style={{ background: 'linear-gradient(160deg, #1e3c72, #2a5298)', borderRadius: '0.5rem' }}
                                >
                                    {loading ? (
                                        <>
                                            <Spinner animation="border" size="sm" className="me-2" />
                                            Connexion en cours...
                                        </>
                                    ) : (
                                        'Se connecter'
                                    )}
                                </Button>
                            </Form>

                            <p className="text-center text-muted small mt-4 mb-0 d-lg-none">
                                © {new Date().getFullYear()} Senegal Soplast SARL
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;